import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { Pagination } from '@/components/shared/pagination';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { api } from '@/lib/api';
import { dataTableMoneyColumnTotals } from '@/lib/money-totals';
import { hasPermission, PERMISSIONS } from '@/lib/permissions';
import { useChurchStore } from '@/stores/church-store';
import { formatDate } from '@/utils/format';

interface Fund {
  id: string;
  name: string;
  fundType: string;
}

interface Contribution {
  id: string;
  fundId: string;
  contributionType?: string;
  amountMinor: number;
  currency: string;
  contributionDate: string;
  method: string;
  receiptNumber: number;
  isVoided: boolean;
  identityHidden: boolean;
}

interface Pledge {
  id: string;
  fundId: string;
  totalAmountMinor: number;
  fulfilledAmountMinor: number;
  remainingAmountMinor: number;
  currency: string;
  frequency: string;
  startDate: string;
  status: string;
  overFulfilled: boolean;
}

interface PageMeta {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

type GivingSection = 'tithes' | 'offerings' | 'pledges';
type RecordGivingIntent = 'tithe' | 'offering';

/**
 * Person-scoped giving: tithes, offerings, and pledges as peer sub-tabs.
 * Recording opens from the member page header (and empty-state CTA).
 */
export const MemberGivingTab = ({
  memberId,
  canRecord = false,
  onRecord,
}: {
  memberId: string;
  canRecord?: boolean;
  onRecord?: (intent?: RecordGivingIntent) => void;
}) => {
  const { t } = useTranslation('people');
  const { t: tGiving } = useTranslation('giving');
  const { t: tProjects } = useTranslation('projects');
  const { t: tCommon } = useTranslation();
  const church = useChurchStore((state) => state.church);
  const membership = useChurchStore((state) => state.membership);

  const [section, setSection] = useState<GivingSection>('tithes');
  const [tithePage, setTithePage] = useState(1);
  const [offeringPage, setOfferingPage] = useState(1);
  const [pledgePage, setPledgePage] = useState(1);
  const pageSize = 25;

  const canViewPledges = hasPermission(membership, PERMISSIONS.GIVING_VIEW);
  const canViewFunds = hasPermission(membership, PERMISSIONS.GIVING_VIEW);

  const funds = useQuery({
    queryKey: ['giving', 'funds', 'all-for-names'],
    queryFn: () => api.get<{ data: Fund[] }>('/giving/funds?pageSize=100'),
    enabled: canViewFunds,
  });

  const tithes = useQuery({
    queryKey: ['giving', 'member-history', memberId, 'tithe', tithePage],
    queryFn: () =>
      api.get<{
        data: Contribution[];
        page: PageMeta;
        totals?: { amountMinor: number; count: number };
      }>(
        `/giving/members/${memberId}/history?${new URLSearchParams({
          kind: 'tithe',
          page: String(tithePage),
          pageSize: String(pageSize),
        }).toString()}`,
      ),
    enabled: section === 'tithes',
  });

  const offerings = useQuery({
    queryKey: ['giving', 'member-history', memberId, 'offering', offeringPage],
    queryFn: () =>
      api.get<{
        data: Contribution[];
        page: PageMeta;
        totals?: { amountMinor: number; count: number };
      }>(
        `/giving/members/${memberId}/history?${new URLSearchParams({
          kind: 'offering',
          page: String(offeringPage),
          pageSize: String(pageSize),
        }).toString()}`,
      ),
    enabled: section === 'offerings',
  });

  const pledges = useQuery({
    queryKey: ['giving', 'member-pledges', memberId, pledgePage],
    queryFn: () =>
      api.get<{ data: Pledge[]; page: PageMeta }>(
        `/giving/pledges?${new URLSearchParams({
          memberId,
          page: String(pledgePage),
          pageSize: String(pageSize),
        }).toString()}`,
      ),
    enabled: canViewPledges && section === 'pledges',
  });

  const fundLabel = (id: string) => {
    const fund = funds.data?.data.find((row) => row.id === id);
    return fund?.name ?? '—';
  };

  const kindLabel = (value: string | undefined) => {
    if (!value) return '—';
    const key = `kinds.${value}` as const;
    const label = tGiving(key as 'kinds.tithe');
    return label !== key ? label : value;
  };

  const contributionColumns = (
    includeKind: boolean,
  ): DataTableColumn<Contribution>[] => [
    {
      id: 'date',
      header: tGiving('contributions.columns.date'),
      cell: (row) => formatDate(row.contributionDate),
    },
    {
      id: 'amount',
      header: tGiving('contributions.columns.amount'),
      money: (row) => ({ amount: row.amountMinor, currency: row.currency }),
    },
    ...(includeKind
      ? [
          {
            id: 'kind',
            header: t('members.detail.giving.columns.kind'),
            cell: (row: Contribution) => kindLabel(row.contributionType),
          } satisfies DataTableColumn<Contribution>,
        ]
      : []),
    {
      id: 'fund',
      header: t('members.detail.giving.columns.fund'),
      cell: (row) => fundLabel(row.fundId),
    },
    {
      id: 'method',
      header: tGiving('contributions.columns.method'),
      cell: (row) => tGiving(`methods.${row.method}` as 'methods.cash'),
      hideBelowMd: true,
    },
    {
      id: 'receipt',
      header: tGiving('contributions.columns.receipt'),
      cell: (row) => tGiving('contributions.receipt', { number: row.receiptNumber }),
      hideBelowMd: true,
    },
    {
      id: 'status',
      header: tGiving('contributions.columns.status'),
      cell: (row) =>
        row.isVoided ? (
          <Badge variant="danger">{tGiving('contributions.voided')}</Badge>
        ) : row.identityHidden ? (
          <Badge variant="neutral">{tGiving('contributions.identityHidden')}</Badge>
        ) : (
          '—'
        ),
    },
  ];

  const titheColumns = useMemo(
    () => contributionColumns(false),
    [funds.data?.data, t, tGiving],
  );
  const offeringColumns = useMemo(
    () => contributionColumns(true),
    [funds.data?.data, t, tGiving],
  );

  const pledgeColumns: DataTableColumn<Pledge>[] = [
    {
      id: 'start',
      header: tProjects('form.startDate'),
      cell: (row) => formatDate(row.startDate),
    },
    {
      id: 'total',
      header: tProjects('pledges.columns.total'),
      money: (row) => ({ amount: row.totalAmountMinor, currency: row.currency }),
    },
    {
      id: 'fulfilled',
      header: tProjects('pledges.columns.fulfilled'),
      money: (row) => ({ amount: row.fulfilledAmountMinor, currency: row.currency }),
    },
    {
      id: 'fund',
      header: t('members.detail.giving.columns.fund'),
      cell: (row) => fundLabel(row.fundId),
    },
    {
      id: 'frequency',
      header: tProjects('pledges.columns.frequency'),
      cell: (row) => tProjects(`frequency.${row.frequency}` as 'frequency.monthly'),
      hideBelowMd: true,
    },
    {
      id: 'status',
      header: tProjects('pledges.columns.status'),
      cell: (row) =>
        row.overFulfilled ? (
          <Badge variant="success">{tProjects('pledges.over')}</Badge>
        ) : (
          <Badge variant="neutral">
            {tProjects(`pledges.status.${row.status}` as 'pledges.status.active')}
          </Badge>
        ),
    },
  ];

  const totalsCurrency = church?.baseCurrency ?? 'GHS';
  const tithePageMeta = tithes.data?.page;
  const offeringPageMeta = offerings.data?.page;
  const pledgePageMeta = pledges.data?.page;

  const paginationLabels = {
    showing: ({ from, to, total }: { from: number; to: number; total: number }) =>
      tCommon('pagination.showing', { from, to, total }),
    perPage: tCommon('pagination.perPage'),
    first: tCommon('pagination.first'),
    previous: tCommon('pagination.previous'),
    next: tCommon('pagination.next'),
    last: tCommon('pagination.last'),
  };

  return (
    <Tabs
      value={section}
      onValueChange={(value) => setSection(value as GivingSection)}
    >
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <TabsList>
          <TabsTrigger value="tithes">{t('members.detail.giving.tabs.tithes')}</TabsTrigger>
          <TabsTrigger value="offerings">{t('members.detail.giving.tabs.offerings')}</TabsTrigger>
          {canViewPledges ? (
            <TabsTrigger value="pledges">{t('members.detail.giving.tabs.pledges')}</TabsTrigger>
          ) : null}
        </TabsList>
      </div>

      <TabsContent value="tithes" className="space-y-3">
        <p className="text-body-sm text-fg-secondary">
          {t('members.detail.giving.tithesDescription')}
        </p>
        <DataTable
          rows={tithes.data?.data ?? []}
          columns={titheColumns}
          rowKey={(row) => row.id}
          loading={tithes.isLoading}
          empty={
            <div className="space-y-2 text-body-sm text-fg-secondary">
              <p>{t('members.detail.giving.tithesEmpty')}</p>
              {canRecord && onRecord ? (
                <Button size="sm" onClick={() => onRecord('tithe')}>
                  {t('members.detail.giving.recordTithe')}
                </Button>
              ) : null}
            </div>
          }
          totals={dataTableMoneyColumnTotals('amount', tithes.data?.totals, totalsCurrency)}
          totalsLabel={tGiving('tithes.totals')}
        />
        {tithePageMeta && tithePageMeta.totalPages > 1 ? (
          <Pagination
            page={tithePageMeta.page}
            pageSize={tithePageMeta.pageSize}
            totalItems={tithePageMeta.totalItems}
            totalPages={tithePageMeta.totalPages}
            onPageChange={setTithePage}
            labels={paginationLabels}
          />
        ) : null}
      </TabsContent>

      <TabsContent value="offerings" className="space-y-3">
        <p className="text-body-sm text-fg-secondary">
          {t('members.detail.giving.offeringsDescription')}
        </p>
        <DataTable
          rows={offerings.data?.data ?? []}
          columns={offeringColumns}
          rowKey={(row) => row.id}
          loading={offerings.isLoading}
          empty={
            <div className="space-y-2 text-body-sm text-fg-secondary">
              <p>{t('members.detail.giving.offeringsEmpty')}</p>
              {canRecord && onRecord ? (
                <Button size="sm" onClick={() => onRecord('offering')}>
                  {t('members.detail.giving.recordOffering')}
                </Button>
              ) : null}
            </div>
          }
          totals={dataTableMoneyColumnTotals('amount', offerings.data?.totals, totalsCurrency)}
          totalsLabel={tGiving('contributions.totals')}
        />
        {offeringPageMeta && offeringPageMeta.totalPages > 1 ? (
          <Pagination
            page={offeringPageMeta.page}
            pageSize={offeringPageMeta.pageSize}
            totalItems={offeringPageMeta.totalItems}
            totalPages={offeringPageMeta.totalPages}
            onPageChange={setOfferingPage}
            labels={paginationLabels}
          />
        ) : null}
      </TabsContent>

      {canViewPledges ? (
        <TabsContent value="pledges" className="space-y-3">
          <p className="text-body-sm text-fg-secondary">
            {t('members.detail.giving.pledgesDescription')}
          </p>
          <DataTable
            rows={pledges.data?.data ?? []}
            columns={pledgeColumns}
            rowKey={(row) => row.id}
            loading={pledges.isLoading}
            empty={
              <p className="text-body-sm text-fg-secondary">
                {t('members.detail.giving.pledgesEmpty')}
              </p>
            }
          />
          {pledgePageMeta && pledgePageMeta.totalPages > 1 ? (
            <Pagination
              page={pledgePageMeta.page}
              pageSize={pledgePageMeta.pageSize}
              totalItems={pledgePageMeta.totalItems}
              totalPages={pledgePageMeta.totalPages}
              onPageChange={setPledgePage}
              labels={paginationLabels}
            />
          ) : null}
        </TabsContent>
      ) : null}
    </Tabs>
  );
};
